import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { WorkCenterDto } from '@app/generated/model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { State } from '@dwo/reducers';
import { EditWorkCenterComponent } from './edit-work-center/edit-workcenter.component';
import { AddWorkCenterComponent } from './add-work-center/add-work-center.component';
import { LoadWorkCenters, FilterWorkCenters } from '@app/core/store/actions/work-center-action';
import { getFilteredWorkCenters, getWorkCenterFilter } from '@app/core/store/selectors/work-center.selector';
import { getResourceState } from '@dwo/selectors';
import { ResourceStatus, ResourceType } from '@dwo/enums';

@Component({
  selector: 'app-site-work-centers',
  templateUrl: './work-centers.component.html',
  styleUrls: ['./work-centers.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkCentersComponent implements OnInit, OnDestroy {
  displayColumns: string[] = ['id', 'name', 'number', 'actions'];
  dataSource: MatTableDataSource<WorkCenterDto> = new MatTableDataSource();
  filter = '';
  loading = false;
  siteId: string;
  private readonly destroy$ = new Subject<void>();

  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor(
    private store: Store<State>,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.siteId = this.route.snapshot.paramMap.get('locationId');
    this.store.dispatch(new LoadWorkCenters(this.siteId));

    this.store
      .select(getFilteredWorkCenters)
      .pipe(takeUntil(this.destroy$))
      .subscribe((workCenters) => {
        this.dataSource.data = workCenters;
      });

    this.store
      .select(getWorkCenterFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filter) => {
        this.filter = filter;
      });

    this.store
      .select(getResourceState, ResourceType.LoadWorkCenters)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.loading = state.status === ResourceStatus.InProgress;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRefresh(): void {
    this.store.dispatch(new LoadWorkCenters(this.siteId));
  }

  openDialog(workCenter: WorkCenterDto): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.disableClose = true;
    dialogConfig.width = '45vw';
    dialogConfig.maxWidth = dialogConfig.width;
    dialogConfig.data = workCenter;
    dialogConfig.panelClass = 'dialog';

    this.dialog
      .open(EditWorkCenterComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(new LoadWorkCenters(this.siteId));
        }
      });
  }

  onAdd(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.disableClose = true;
    dialogConfig.width = '45vw';
    dialogConfig.maxWidth = dialogConfig.width;
    dialogConfig.data = { siteId: this.siteId };
    dialogConfig.panelClass = 'dialog';

    this.dialog
      .open(AddWorkCenterComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(new LoadWorkCenters(this.siteId));
        }
      });
  }

  onFilter(filter: string): void {
    this.store.dispatch(new FilterWorkCenters(filter));
  }
}
